// Full-page live per-device metrics for an ACTIVE igloo session.
// Rendered via AdminDashboard's early-return "viewer" pattern (like InstantLogViewer),
// NOT an overlay — reached from the "Metrics" button on an igloo line.
//
// Data sources (all live via onSnapshot):
//  - iglooSessionsLive/{iglooId}.participants -> the families currently in the session
//    (last interval has no leftAt).
//  - deviceManagement/{familyId}/log/{zoom,livekit,camera,system} -> the continuously
//    updated latest-value mirror. Namespace split: each backend writes ONLY its own
//    doc (Zoom -> zoom.video.*/audio.*; LiveKit -> livekit send./recv./audio.* with
//    WebRTC definitions), so transport rows resolve their (doc, fieldPath) per the
//    namespace each device is ACTUALLY writing — declared by the device itself in
//    system.call.activeBackend at session build (this webapp reads no individual
//    setting). Resolved PER DEVICE, not per session — see deviceBackend/resolveRow.
//    Metric key names are TVApp-defined, so anything not currently reported renders
//    as "—" rather than erroring.
import React, { useEffect, useState } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { Button } from '../ui/button';
import { ArrowLeft, Activity, ArrowUp, ArrowDown, Volume2, Wifi, Cpu, Gauge } from 'lucide-react';

const LOG_DOCS = ['zoom', 'camera', 'system', 'livekit'];

// Display groups. Each row: [label, logDoc, fieldPath (string | [w,h]), unit].
// logDoc 'transport' is a pseudo-doc resolved per backend by resolveRow: the fieldPath
// is the canonical zoom-style name; on LiveKit igloos it maps to the livekit doc's key.
// Order matters: the two video groups render FIRST — they are what an at-a-glance
// health read needs. Receive before send (on LiveKit the receive story is the
// per-remote section, which renders above these groups — see the card body).
const GROUPS = [
  // Zoom-only: on LiveKit this is just the first inbound stream (not a true aggregate) and is
  // superseded by the per-remote section (rendered at the top of the card on LiveKit), so it's
  // hidden there to avoid a misleading duplicate.
  // (Plain 'zoom' doc, never rendered on LiveKit — no resolution needed.)
  { title: 'Video · recv', zoomOnly: true, rows: [
    ['fps', 'zoom', 'video.recv.fps', ''],
    ['resolution', 'zoom', ['video.recv.frameWidth', 'video.recv.frameHeight'], 'res'],
    ['latency', 'zoom', 'video.recv.latencyMs', 'ms'],
    ['jitter', 'zoom', 'video.recv.jitterMs', 'ms'],
    ['loss', 'zoom', 'video.recv.packetLossAvg', '%'],
    ['downlink', 'zoom', 'video.remote.bpf', 'B/f'],
  ] },
  { title: 'Video · send', rows: [
    ['fps', 'transport', 'video.send.fps', ''],
    ['resolution', 'transport', ['video.send.frameWidth', 'video.send.frameHeight'], 'res'],
    ['latency', 'transport', 'video.send.latencyMs', 'ms'],
    ['jitter', 'transport', 'video.send.jitterMs', 'ms'],
    ['loss', 'transport', 'video.send.packetLossAvg', '%'],
    ['uplink', 'transport', 'video.local.bpf', 'B/f'],
  ] },
  { title: 'Audio', rows: [
    ['send latency', 'transport', 'audio.send.latencyMs', 'ms'],
    ['send jitter', 'transport', 'audio.send.jitterMs', 'ms'],
    ['recv latency', 'transport', 'audio.recv.latencyMs', 'ms'],
    ['recv jitter', 'transport', 'audio.recv.jitterMs', 'ms'],
  ] },
  { title: 'Network', rows: [
    ['Wi-Fi RSSI', 'system', 'network.wifiRssiDbm', 'dBm'],
    ['Wi-Fi link', 'system', 'network.wifiLinkMbps', 'Mbps'],
    ['Wi-Fi SNR', 'system', 'network.wifiSnrDb', 'dB'],
    ['tx rate', 'system', 'network.txPktsPerSec', 'p/s'],
    ['rx rate', 'system', 'network.rxPktsPerSec', 'p/s'],
  ] },
  { title: 'Camera / System', rows: [
    ['camera fps', 'camera', 'threadFps', ''],
    ['CPU big', 'system', 'cpuLoadHighPerformance', '%'],
    ['CPU little', 'system', 'cpuLoadLowPerformance', '%'],
    ['SoC temp', 'system', 'temp', '°C'],
  ] },
  // LiveKit-native diagnostics the backend already emits under livekit.* — only shown on
  // LiveKit sessions (Zoom doesn't populate these keys). qualityLimitationReason is a string.
  { title: 'Diagnostics · LiveKit', livekitOnly: true, rows: [
    ['send bitrate', 'livekit', 'send.throughputBps', 'bps'],
    ['recv bitrate', 'livekit', 'recv.throughputBps', 'bps'],
    // Capture-side pipeline output size (echoes the livekitSendResolution setting).
    // The send group's resolution row shows the WIRE size instead; when this is
    // larger than that, congestion control is degrading the encode.
    ['capture res', 'livekit', ['send.width', 'send.height'], 'res'],
    ['quality limit', 'livekit', 'send.qualityLimitationReason', ''],
    ['NACKs ↑', 'livekit', 'send.nackCount', ''],
    ['PLIs ↑', 'livekit', 'send.pliCount', ''],
    ['avail. up', 'livekit', 'send.availableOutgoingBitrate', 'bps'],
    ['freezes ↓', 'livekit', 'recv.freezeCount', ''],
    ['freeze time', 'livekit', 'recv.totalFreezesDurationS', 's'],
    ['jitter buf', 'livekit', 'recv.jitterBufferDelayMs', 'ms'],
    ['avail. down', 'livekit', 'recv.availableIncomingBitrate', 'bps'],
  ] },
];

// One icon per group header.
const GROUP_ICONS = {
  'Video · send': ArrowUp,
  'Video · recv': ArrowDown,
  'Audio': Volume2,
  'Network': Wifi,
  'Camera / System': Cpu,
  'Diagnostics · LiveKit': Gauge,
};

// Per-device accent (card top strip), chosen by column index.
const DEVICE_ACCENTS = [
  'from-blue-500 to-indigo-500',
  'from-emerald-500 to-teal-500',
  'from-amber-500 to-orange-500',
  'from-fuchsia-500 to-purple-500',
];

// Readable last-resort label when a family has no name — a short id beats a 36-char UUID.
function shortId(id) {
  return (typeof id === 'string' && id.length > 12) ? `${id.slice(0, 8)}…` : String(id);
}

function fmtNum(v) {
  if (typeof v !== 'number' || !isFinite(v)) return String(v);
  if (Math.abs(v) >= 100000) return `${(v / 1000).toFixed(0)}k`;
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(1);
}

// Namespace split: Zoom transport metrics live in the zoom doc (video.*/audio.* keys);
// LiveKit's live in the livekit doc under its convention (video implied — send.latencyMs
// like send.fps; audio keeps audio.send./audio.recv.; bpf is send.bpf/recv.bpf).
// Rows declare logDoc 'transport' + the canonical zoom-style fieldPath; this maps the
// LiveKit side. Unmapped canonical names fall through unchanged (audio.* is identical).
//
// Send resolution maps to send.wireWidth/wireHeight — the WIRE resolution (largest
// actively-encoding simulcast layer from getStats), NOT send.width/height, which is the
// capture-side pipeline size and just echoes the livekitSendResolution setting even while
// congestion control is degrading the actual encode. The pipeline size still shows as
// "capture res" in the LiveKit diagnostics group; a gap between the two IS the encoder
// shedding quality. Devices on builds that predate the wire keys render "—" here.
const LIVEKIT_TRANSPORT_KEYS = {
  'video.send.fps': 'send.fps',
  'video.send.frameWidth': 'send.wireWidth',
  'video.send.frameHeight': 'send.wireHeight',
  'video.send.latencyMs': 'send.latencyMs',
  'video.send.jitterMs': 'send.jitterMs',
  'video.send.packetLossAvg': 'send.packetLossAvg',
  'video.local.bpf': 'send.bpf',
};

// Resolve a row's (logDoc, fieldPath) for the namespace in use. NO fallback-to-zoom for
// LiveKit rows: the zoom doc is merge-only and retains stale values from past sessions,
// so a blank ("—") is honest where a fallback would look plausible and be wrong.
function resolveRow(backend, logDoc, fieldPath) {
  if (logDoc !== 'transport') return [logDoc, fieldPath];
  if (backend !== 'LiveKit') return ['zoom', fieldPath];
  const map = (p) => LIVEKIT_TRANSPORT_KEYS[p] || p;
  return ['livekit', Array.isArray(fieldPath) ? fieldPath.map(map) : map(fieldPath)];
}

// Which metric namespace THIS device is writing — resolved per device, not per
// session, from the device's own declaration: HigCallController writes
// system.call.activeBackend at session build, before any transport metrics flow.
// Presence of livekit keys is NOT a usable signal on its own: the log mirror
// docs are merge-only and never cleared, so a device that has ever run one
// LiveKit session keeps a populated livekit doc forever, and a later Zoom
// session would render the stale LiveKit numbers (observed 2026-08-11: a Zoom
// call showing a stuck 1920×1080 wire resolution from an old LiveKit session).
// The presence heuristic remains only as a fallback for devices on builds that
// predate the field. Per-device resolution also makes a mixed session — one
// device that missed a backend flip — visible instead of absorbed into a guess.
function deviceBackend(deviceLog) {
  const declared = deviceLog && deviceLog.system && deviceLog.system['call.activeBackend'];
  if (declared === 'livekit') return 'LiveKit';
  if (declared === 'zoom') return 'Zoom';
  return (deviceLog && deviceLog.livekit && Object.keys(deviceLog.livekit).length > 0)
    ? 'LiveKit' : 'Zoom';
}

// Epoch ms of the device's last system.onlinePing (a Firestore Timestamp written
// every 10 s while the app is up and online), or null when absent/unparsable.
function lastPingMs(deviceLog) {
  const v = deviceLog && deviceLog.system && deviceLog.system.onlinePing;
  if (v && typeof v.toMillis === 'function') return v.toMillis();     // Firestore Timestamp
  if (typeof v === 'number' && isFinite(v)) return v;                 // defensive: plain epoch ms
  return null;
}

// Colour for the ping age: cadence is 10 s, so anything ≤20 s is healthy; one
// missed ping reads plain; ≥45 s means the device has stopped reporting.
function pingColor(ageS) {
  if (ageS <= 20) return 'text-emerald-600';
  if (ageS < 45) return '';
  return 'text-red-600';
}

// Raw scalar for a metric (or null for absent / non-scalar like resolution) — drives colouring.
function getRaw(deviceLog, logDoc, fieldPath) {
  const bag = deviceLog && deviceLog[logDoc];
  if (!bag || Array.isArray(fieldPath)) return null;
  const v = bag[fieldPath];
  return (typeof v === 'number' && isFinite(v)) ? v : null;
}

// Display text WITHOUT unit (number, or WxH for resolution). '—' when absent.
function cellText(deviceLog, logDoc, fieldPath, unit) {
  const bag = deviceLog && deviceLog[logDoc];
  if (!bag) return '—';
  if (unit === 'res' && Array.isArray(fieldPath)) {
    const w = bag[fieldPath[0]];
    const h = bag[fieldPath[1]];
    return (w != null && h != null) ? `${w}×${h}` : '—';
  }
  const v = bag[fieldPath];
  if (v == null) return '—';
  // 'bps' rows are stored in bits/s and shown as Mbps; 'Mbps' rows (e.g. Wi-Fi link) are already Mbps.
  if (unit === 'bps' && typeof v === 'number') return (v / 1e6).toFixed(2);
  return fmtNum(v);
}

// Unit shown to the user (bps-stored rows display as Mbps).
function displayUnit(unit) {
  return unit === 'bps' ? 'Mbps' : unit;
}

// Threshold colouring — green (healthy) / amber (degraded) / red (bad); plain =
// no opinion. All thresholds live here so tuning is a one-line edit per metric.
// The fps/latency/jitter/loss rules also colour the per-remote recv rows, whose
// labels match. Informed by observed fleet behaviour: healthy calls run 24-30fps
// with <60ms latency and ~10ms jitter; degraded joins showed 100-200ms recv
// jitter and 1-9fps trickle.
function valueColor(label, unit, raw) {
  if (raw == null) return '';
  if (label.includes('fps')) { // video send/recv, camera thread, per-remote
    if (raw >= 20) return 'text-emerald-600';
    return raw >= 10 ? 'text-amber-600' : 'text-red-600';
  }
  if (label.includes('latency') || label.includes('RTT')) {
    if (raw <= 75) return 'text-emerald-600';
    if (raw <= 150) return '';
    return raw <= 250 ? 'text-amber-600' : 'text-red-600';
  }
  if (label.includes('jitter')) { // send/recv jitter and the recv jitter buffer
    if (raw <= 30) return 'text-emerald-600';
    if (raw <= 80) return '';
    return raw <= 150 ? 'text-amber-600' : 'text-red-600';
  }
  if (unit === '%' && label.includes('loss')) {
    if (raw <= 0) return 'text-emerald-600';
    return raw < 2 ? 'text-amber-600' : 'text-red-600';
  }
  if (label.includes('CPU')) {
    if (raw <= 60) return 'text-emerald-600';
    if (raw <= 80) return '';
    return raw <= 90 ? 'text-amber-600' : 'text-red-600';
  }
  if (unit === 'dBm') { // Wi-Fi RSSI
    if (raw >= -60) return 'text-emerald-600';
    if (raw >= -70) return '';
    if (raw >= -80) return 'text-amber-600';
    return 'text-red-600';
  }
  if (label === 'Wi-Fi link') { // negotiated link rate, Mbps
    if (raw >= 80) return 'text-emerald-600';
    if (raw >= 25) return '';
    return raw >= 10 ? 'text-amber-600' : 'text-red-600';
  }
  if (label === 'Wi-Fi SNR') {
    if (raw >= 25) return 'text-emerald-600';
    if (raw >= 15) return '';
    return raw >= 10 ? 'text-amber-600' : 'text-red-600';
  }
  if (unit === '°C') { // SoC temp
    if (raw >= 75) return 'text-red-600';
    if (raw >= 60) return 'text-amber-600';
    return 'text-emerald-600';
  }
  if (label.includes('freeze')) {
    return raw > 0 ? 'text-amber-600' : 'text-emerald-600'; // any video freeze is bad
  }
  return '';
}

// String metrics can't go through valueColor (getRaw yields numbers only) — the
// one that matters is LiveKit's qualityLimitationReason: 'none' is the healthy
// state, anything else ('cpu' / 'bandwidth') is the encoder telling us it is
// actively degrading the send.
function stringColor(label, text) {
  if (label === 'quality limit') {
    return text === 'none' ? 'text-emerald-600' : 'text-amber-600';
  }
  return '';
}

// Compact per-remote recv rows (multi-party): each remote's downlink into this device.
// 'res' rows carry a [widthKey, heightKey] pair and render as W×H — the one non-scalar
// row shape (mirrors the GROUPS convention). The resolution here is what the SFU is
// actually delivering FROM that remote (its simulcast layer as received), which is the
// most direct read of a remote's effective send quality into this device.
const PER_REMOTE_METRICS = [
  ['fps', 'fps', ''],
  ['resolution', ['width', 'height'], 'res'],
  ['loss', 'loss', '%'],
  ['jitter', 'jitterMs', 'ms'],
  ['bitrate', 'throughputBps', 'bps'],
  ['freezes', 'freezeCount', ''],
];

// Extract per-remote recv streams from a device's `livekit` log bag. Per-remote keys look like
// `recv.<identity>.<metric>` (3 dot-parts); the aggregate keys (`recv.<metric>`) have only two.
function perRemoteRecv(livekitBag) {
  if (!livekitBag) return [];
  const known = new Set(['fps', 'width', 'height', 'loss', 'jitterMs', 'bpf',
    'throughputBps', 'freezeCount', 'totalFreezesDurationS', 'jitterBufferDelayMs']);
  const byId = {};
  Object.keys(livekitBag).forEach((k) => {
    const parts = k.split('.');
    if (parts.length === 3 && parts[0] === 'recv' && known.has(parts[2])) {
      const id = parts[1];
      byId[id] = byId[id] || { identity: id };
      byId[id][parts[2]] = livekitBag[k];
    }
  });
  return Object.values(byId).sort((a, b) => a.identity.localeCompare(b.identity));
}

export default function IglooMetricsViewer({ igloo, onBack }) {
  const iglooId = igloo.id;
  const iglooName = igloo.iglooName || igloo.name || igloo.id;
  const [devices, setDevices] = useState([]);   // [{ familyId, name }]
  const [logData, setLogData] = useState({});   // { familyId: { zoom, livekit, camera, system } }
  const [noSession, setNoSession] = useState(false);
  const [loadError, setLoadError] = useState(false); // session listener failed (permission/transient)

  // 1-second clock for the per-device "last ping Xs ago" readout — the only
  // state in this component that changes without a Firestore snapshot.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Which devices are live right now (from the session doc).
  useEffect(() => {
    const db = getFirestore(app);
    let latest = 0; // guards against out-of-order async name resolution + post-unmount sets
    const unsub = onSnapshot(
      doc(db, 'iglooSessionsLive', iglooId),
      async (snap) => {
        const gen = ++latest;
        if (gen === latest) setLoadError(false);
        if (!snap.exists()) {
          if (gen === latest) { setDevices([]); setNoSession(true); }
          return;
        }
        const data = snap.data() || {};
        const participants = Array.isArray(data.participants) ? data.participants : [];
        const liveIds = [...new Set(
          participants
            .filter((p) => {
              const iv = p.intervals && p.intervals[p.intervals.length - 1];
              return iv && !iv.leftAt;
            })
            .map((p) => p.familyId)
            .filter(Boolean),
        )];
        const resolved = await Promise.all(liveIds.map(async (fid) => {
          try {
            const f = await getDoc(doc(db, 'familyInfo', fid));
            const nm = (f.exists() && typeof f.data().name === 'string' && f.data().name.trim())
              ? f.data().name.trim()
              : null;
            return { familyId: fid, name: nm || shortId(fid) };
          } catch (e) {
            console.error('IglooMetricsViewer: familyInfo name resolve failed', fid, e);
            return { familyId: fid, name: shortId(fid) };
          }
        }));
        // Only the newest snapshot's resolution wins (rapid snapshots can finish out of order);
        // also drops any completion after unmount.
        if (gen === latest) { setNoSession(false); setDevices(resolved); }
      },
      (e) => { latest++; console.error('IglooMetricsViewer: iglooSessionsLive listener error', iglooId, e); setDevices([]); setNoSession(false); setLoadError(true); },
    );
    return () => { latest = Infinity; unsub(); };
  }, [iglooId]);

  // Per-device live-log listeners (re-subscribe when the live device set changes).
  const deviceKey = devices.map((d) => d.familyId).sort().join(',');
  useEffect(() => {
    const db = getFirestore(app);
    const ids = deviceKey ? deviceKey.split(',') : [];
    const unsubs = [];
    ids.forEach((fid) => {
      LOG_DOCS.forEach((logType) => {
        const unsub = onSnapshot(
          doc(db, 'deviceManagement', fid, 'log', logType),
          (snap) => {
            setLogData((prev) => ({
              ...prev,
              [fid]: { ...(prev[fid] || {}), [logType]: snap.exists() ? snap.data() : {} },
            }));
          },
          (e) => console.error('IglooMetricsViewer: log listener error', fid, logType, e),
        );
        unsubs.push(unsub);
      });
    });
    return () => unsubs.forEach((u) => u());
  }, [deviceKey]);

  const cols = Math.min(Math.max(devices.length, 1), 3); // 3 columns max; a 4th/5th device wraps to a new row
  // Resolve a remote's participant identity (== familyId) to a live device name, for the per-remote rows.
  const nameByFamilyId = Object.fromEntries(devices.map((d) => [d.familyId, d.name]));
  const hasGrid = !loadError && !noSession && devices.length > 0;

  return (
    <div className="min-h-screen bg-muted/30 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="sm" onClick={onBack} className="flex items-center gap-2 flex-shrink-0">
            <ArrowLeft className="w-4 h-4" /> Back to Igloos
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Activity className="w-5 h-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground leading-none mb-1">Live session</p>
              <h2 className="text-lg font-semibold truncate leading-tight">{iglooName}</h2>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {devices.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              {devices.length} live
            </span>
          )}
        </div>
      </div>

      {/* Terminal / transient states */}
      {loadError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
          <p className="text-sm text-destructive">
            Couldn't load live session data — the session listener failed (permission or transient error).
            See the browser console for details.
          </p>
        </div>
      )}
      {!loadError && noSession && (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Activity className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No active session for this igloo right now.</p>
        </div>
      )}
      {!loadError && !noSession && devices.length === 0 && (
        <div className="rounded-xl border bg-card p-12 text-center">
          <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Waiting for live participants…</p>
        </div>
      )}

      {/* Per-device metric grid */}
      {hasGrid && (
        <div className="overflow-x-auto pb-1">
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(240px, 1fr))` }}
          >
            {devices.map((d, i) => {
              // Per-device: which namespace this device declares it is writing, and
              // how long since its last onlinePing (10 s cadence — a growing age
              // means the device has stopped reporting, whatever the metrics say).
              const backend = deviceBackend(logData[d.familyId]);
              const pingMs = lastPingMs(logData[d.familyId]);
              const pingAgeS = pingMs != null ? Math.max(0, Math.round((nowMs - pingMs) / 1000)) : null;
              return (
              <div key={d.familyId} className="rounded-xl border bg-card shadow-sm overflow-hidden flex flex-col">
                <div className={`h-1 bg-gradient-to-r ${DEVICE_ACCENTS[i % DEVICE_ACCENTS.length]}`} />
                <div className="px-4 py-3 border-b flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate leading-tight">{d.name}</p>
                    <p className="text-[11px] font-mono text-muted-foreground truncate">{d.familyId}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">{backend}</p>
                    <p className={`text-[11px] font-mono tabular-nums leading-tight ${pingAgeS == null ? 'text-muted-foreground/50' : pingColor(pingAgeS)}`}>
                      {pingAgeS == null ? 'ping —' : `ping ${pingAgeS}s ago`}
                    </p>
                  </div>
                </div>
                <div className="divide-y">
                  {/* Receive-first ordering: on LiveKit the per-remote recv section IS the
                      receive view (the aggregate group is zoom-only), so it leads the card. */}
                  {backend === 'LiveKit' && (() => {
                    // Only remotes that are LIVE participants right now (this doc is a merge-only mirror
                    // that retains keys from departed remotes / past sessions), and never self.
                    const remotes = perRemoteRecv(logData[d.familyId]?.livekit)
                      .filter((r) => nameByFamilyId[r.identity] && r.identity !== d.familyId);
                    if (remotes.length === 0) return null;
                    return (
                      <div className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                          <ArrowDown className="w-3.5 h-3.5" /> Video · recv (per remote)
                        </div>
                        {(() => {
                          // Transport RTT is per-connection (same for every remote), so show it once here
                          // rather than per-remote — the one recv metric the per-remote rows can't carry.
                          // (This block renders only on LiveKit, so read the livekit doc directly.)
                          const rtt = logData[d.familyId]?.livekit?.['recv.latencyMs'];
                          return typeof rtt === 'number' ? (
                            <div className="flex items-baseline justify-between text-sm pb-1.5 mb-1.5 border-b">
                              <span className="text-muted-foreground">recv RTT <span className="text-[11px]">(transport)</span></span>
                              <span className={`font-mono tabular-nums ${valueColor('RTT', 'ms', rtt)}`}>
                                {fmtNum(rtt)}<span className="text-[11px] text-muted-foreground ml-0.5">ms</span>
                              </span>
                            </div>
                          ) : null;
                        })()}
                        <div className="space-y-2">
                          {remotes.map((r) => (
                            <div key={r.identity} className="rounded-md bg-muted/40 px-2 py-1.5">
                              <p className="text-xs font-medium truncate mb-0.5">
                                {nameByFamilyId[r.identity] || shortId(r.identity)}
                              </p>
                              <div className="grid grid-cols-2 gap-x-4">
                                {PER_REMOTE_METRICS.map(([label, key, unit]) => {
                                  // 'res' rows: key is a [widthKey, heightKey] pair, shown as W×H
                                  // (no unit suffix, no threshold colouring — same as GROUPS rows).
                                  if (unit === 'res') {
                                    const w = r[key[0]];
                                    const h = r[key[1]];
                                    const ok = typeof w === 'number' && w > 0 && typeof h === 'number' && h > 0;
                                    return (
                                      <div key={label} className="flex items-baseline justify-between text-sm">
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className={`font-mono tabular-nums ${ok ? '' : 'text-muted-foreground/50'}`}>
                                          {ok ? `${w}×${h}` : '—'}
                                        </span>
                                      </div>
                                    );
                                  }
                                  const raw = (typeof r[key] === 'number' && isFinite(r[key])) ? r[key] : null;
                                  const text = raw == null ? '—'
                                    : (unit === 'bps' ? (raw / 1e6).toFixed(2) : fmtNum(raw));
                                  const color = raw == null ? 'text-muted-foreground/50' : valueColor(label, unit, raw);
                                  return (
                                    <div key={label} className="flex items-baseline justify-between text-sm">
                                      <span className="text-muted-foreground">{label}</span>
                                      <span className={`font-mono tabular-nums ${color}`}>
                                        {text}
                                        {raw != null && unit && <span className="text-[11px] text-muted-foreground ml-0.5">{displayUnit(unit)}</span>}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {GROUPS
                    .filter((g) => (!g.livekitOnly || backend === 'LiveKit') && (!g.zoomOnly || backend === 'Zoom'))
                    .map((g) => {
                    const Icon = GROUP_ICONS[g.title] || Activity;
                    return (
                      <div key={g.title} className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                          <Icon className="w-3.5 h-3.5" />
                          {g.title}
                        </div>
                        <div>
                          {g.rows.map(([label, rowDoc, rowPath, unit]) => {
                            const [logDoc, fieldPath] = resolveRow(backend, rowDoc, rowPath);
                            const text = cellText(logData[d.familyId], logDoc, fieldPath, unit);
                            const missing = text === '—';
                            const raw = getRaw(logData[d.familyId], logDoc, fieldPath);
                            const color = missing
                              ? 'text-muted-foreground/50'
                              : (valueColor(label, unit, raw) || stringColor(label, text));
                            const showUnit = !missing && unit && unit !== 'res';
                            return (
                              <div key={label} className="flex items-baseline justify-between gap-2 py-0.5 text-sm">
                                <span className="text-muted-foreground">{label}</span>
                                <span className={`font-mono tabular-nums ${color}`}>
                                  {text}
                                  {showUnit && <span className="text-[11px] text-muted-foreground ml-0.5">{displayUnit(unit)}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4">
        Live from <span className="font-mono">deviceManagement/&#123;familyId&#125;/log</span> · updates as devices
        report · "—" = metric not currently reported. Each card resolves its metric namespace from the device's
        own <span className="font-mono">call.activeBackend</span> declaration (namespace split — each backend owns
        its own keys and definitions), shown in the card corner with the age of the device's last online ping.
      </p>
    </div>
  );
}
